import { ref } from 'vue';
import { resolveLocalized, type LocalizedString } from '@vp/shared-types';
import ru from './ru';
import en from './en';
import { profileLocal } from '../profiles/local';

/** The language last used on this device: for the screens shown before a profile is open. */
const DEVICE_LOCALE = 'vp.locale.device';

/**
 * Localization (§16.1). Strings live outside the logic; adding a language means
 * adding a message file. Module strings are registered per module.
 */
export type Locale = 'ru' | 'en';
export const LOCALES: { id: Locale; name: string }[] = [
  { id: 'ru', name: 'Русский' },
  { id: 'en', name: 'English' },
];

type Tree = { [k: string]: string | Tree };

function flatten(tree: Tree, prefix = '', out: Record<string, string> = {}): Record<string, string> {
  for (const [k, v] of Object.entries(tree)) {
    const key = prefix ? prefix + '.' + k : k;
    if (typeof v === 'string') out[key] = v;
    else flatten(v, key, out);
  }
  return out;
}

const catalogs: Record<Locale, Record<string, string>> = {
  ru: flatten(ru as unknown as Tree),
  en: flatten(en as unknown as Tree),
};

/** Per-module message catalogs: moduleId → locale → key → text. */
const moduleCatalogs = new Map<string, Record<string, Record<string, string>>>();

function detect(): Locale {
  try {
    // the profile's language, else the one last used on this device (screens before a profile is open)
    const saved = profileLocal.getItem('vp.locale') ?? localStorage.getItem(DEVICE_LOCALE);
    if (saved === 'ru' || saved === 'en') return saved;
  } catch {
    /* storage may be unavailable */
  }
  const nav = typeof navigator !== 'undefined' ? navigator.language : 'ru';
  return nav.startsWith('ru') ? 'ru' : 'en';
}

export const locale = ref<Locale>(detect());

/** A profile was opened: its language. */
export function reloadLocaleForProfile(): void {
  locale.value = detect();
  if (typeof document !== 'undefined') document.documentElement.lang = locale.value;
}

export function setLocale(l: Locale): void {
  locale.value = l;
  try {
    profileLocal.setItem('vp.locale', l);
    localStorage.setItem(DEVICE_LOCALE, l);
  } catch {
    /* ignore */
  }
  document.documentElement.lang = l;
}

function interpolate(s: string, params?: Record<string, string | number>): string {
  if (!params) return s;
  return s.replace(/\{(\w+)\}/g, (m, k: string) => (params[k] !== undefined ? String(params[k]) : m));
}

export function t(key: string, params?: Record<string, string | number>): string {
  const l = locale.value;
  const s = catalogs[l][key] ?? catalogs.en[key] ?? key;
  return interpolate(s, params);
}

/** Plural-aware message: forms separated by "|" (ru: one|few|many, en: one|other). */
export function tn(key: string, n: number, params?: Record<string, string | number>): string {
  const forms = t(key, { n: n.toLocaleString(locale.value === 'ru' ? 'ru-RU' : 'en-US'), ...params }).split('|');
  const cat = new Intl.PluralRules(locale.value).select(n);
  const idx = locale.value === 'ru' ? (cat === 'one' ? 0 : cat === 'few' ? 1 : 2) : cat === 'one' ? 0 : 1;
  return forms[Math.min(idx, forms.length - 1)];
}

export function hasKey(key: string): boolean {
  return key in catalogs[locale.value];
}

export function registerModuleMessages(moduleId: string, messages: Record<string, Record<string, string>>): void {
  moduleCatalogs.set(moduleId, messages);
}

export function unregisterModuleMessages(moduleId: string): void {
  moduleCatalogs.delete(moduleId);
}

/**
 * Localize a module-provided string: a LocalizedString object, or a `%key%`
 * reference into the module's locales/<lang>.json catalog.
 */
export function tm(moduleId: string, s: LocalizedString | undefined): string {
  if (s === undefined) return '';
  if (typeof s === 'string') {
    const m = /^%([\w.-]+)%$/.exec(s);
    if (m) {
      const cat = moduleCatalogs.get(moduleId);
      return cat?.[locale.value]?.[m[1]] ?? cat?.en?.[m[1]] ?? m[1];
    }
    return s;
  }
  return resolveLocalized(s, locale.value);
}

export function typeLabel(type: string, moduleName?: (type: string) => string | undefined): string {
  const k = 'types.' + type;
  if (hasKey(k)) return t(k);
  return moduleName?.(type) ?? type;
}

export function formatBytes(n: number | undefined): string {
  if (n === undefined || !Number.isFinite(n)) return '—';
  const u = locale.value === 'ru' ? ['Б', 'КБ', 'МБ', 'ГБ', 'ТБ'] : ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0;
  while (n >= 1024 && i < u.length - 1) {
    n /= 1024;
    i++;
  }
  return `${n.toFixed(n < 10 && i > 0 ? 1 : 0)} ${u[i]}`;
}

export function formatDate(ts: number): string {
  return new Date(ts).toLocaleString(locale.value === 'ru' ? 'ru-RU' : 'en-US', { dateStyle: 'medium', timeStyle: 'short' });
}
